import React from 'react';
import { Text, Divider } from 'wix-ui-tpa/cssVars';
import { WidgetProps, useEnvironment } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { Status } from '@wix/ambassador-loyalty-v1-tier/types';

import settingsParams from '../settingsParams';
import { style, classes } from './widget.st.css';
import { EmptyState } from './empty-state';
import { PointsCard } from './points-card';
import { RewardsList } from './rewards-list';
import { RewardsTabState } from '../../../types/settings';
import { ErrorState } from './error-state';
import { ModalProvider } from './hocs/modal-context';
import { WidgetContextProvider } from './hocs/widget-context/widget-context';
import { getActionHandlers } from './store';
import {
  AccountConfigProps,
  CouponsConfigProps,
  LoyaltyProgramConfigProps,
  ToastConfigProps,
  TiersProgramConfigProps,
  AppInstallStatusProps,
  LocaleProps,
} from './store/slices';
import { RewardsConfigProps } from './store/slices/rewards/slice';
import { DataHook } from '../../../types/data-hook';
import { Toast } from './toast';
import { TierCard } from './tier-card';

export type ControllerProps = {
  hasError: boolean;
  isWidgetPlugin: boolean;
  rewardsTabState: RewardsTabState;
  accountConfig: AccountConfigProps;
  accountHandlers: ReturnType<typeof getActionHandlers>['accountHandlers'];
  couponsConfig: CouponsConfigProps;
  couponsHandlers: ReturnType<typeof getActionHandlers>['couponsHandlers'];
  rewardsConfig: RewardsConfigProps;
  rewardsHandlers: ReturnType<typeof getActionHandlers>['rewardsHandlers'];
  navigationHandlers: ReturnType<typeof getActionHandlers>['navigationHandlers'];
  loyaltyProgramConfig: LoyaltyProgramConfigProps;
  toastConfig: ToastConfigProps;
  toastHandlers: ReturnType<typeof getActionHandlers>['toastHandlers'];
  tiersProgramConfig: TiersProgramConfigProps;
  appInstallStatusConfig: AppInstallStatusProps;
  localeConfig: LocaleProps;
};

const Widget: React.FC<WidgetProps<ControllerProps>> = (props) => {
  const settings = useSettings();
  const { isRTL, isMobile } = useEnvironment();
  const { hasError, isWidgetPlugin, appInstallStatusConfig, tiersProgramConfig, rewardsConfig, rewardsTabState } =
    props;

  if (hasError) {
    return <ErrorState />;
  }

  const { isLoyaltyAppInstalled } = appInstallStatusConfig;
  const showTiers = tiersProgramConfig?.programSettings?.status === Status.ACTIVE;
  const showEmptyState = rewardsConfig.simpleRewards.length === 0 || rewardsTabState === RewardsTabState.Empty;

  return (
    <div dir={isRTL ? 'rtl' : undefined}>
      <WidgetContextProvider {...props}>
        <ModalProvider>
          <Toast />
          <div className={style(classes.root, { isWidgetPlugin, isMobile })} data-hook={DataHook.MyRewardsWrapper}>
            <Text tagName="h2" data-hook={DataHook.PageTitle} className={classes.pageTitle}>
              {settings.get(settingsParams.pageTitle)}
            </Text>
            <Text tagName="p" data-hook={DataHook.PageDescription} className={classes.pageDescription}>
              {settings.get(settingsParams.pageDescription)}
            </Text>
            {isLoyaltyAppInstalled && (
              <div
                data-hook={DataHook.PointsSection}
                className={style(classes.pointsSection, {
                  showTiers,
                })}
              >
                {showTiers && <TierCard />}
                <PointsCard />
              </div>
            )}
            {!isLoyaltyAppInstalled && !showEmptyState && <Divider className={classes.divider} />}
            <div data-hook={DataHook.RewardsSection}>{showEmptyState ? <EmptyState /> : <RewardsList />}</div>
          </div>
        </ModalProvider>
      </WidgetContextProvider>
    </div>
  );
};

export default Widget;
